import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toAbsoluteUrl } from "../../../../../../../../_metronic/_helpers";

const STORAGE_KEY = "oversight.aiWorkspace.v1";
const ASSISTANT_NAME = "Trove Chat";
const BRAND_GREEN = "#22A455";
const CHAT_ICON_URL = "/media/logos/logo-letter-2.png";

const safeJsonParse = (value) => {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const uuidv4 = () =>
    "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });

const nowIso = () => new Date().toISOString();

const normalizeAnswer = (payload) => {
    const candidate =
        payload?.result?.response ??
        payload?.result?.summary ??
        payload?.response ??
        payload?.summary ??
        payload?.data?.result?.response ??
        payload?.data?.result?.summary ??
        payload?.data?.summary ??
        payload?.data?.answer ??
        payload?.data?.message ??
        payload?.answer ??
        payload?.message ??
        payload?.data ??
        payload;

    if (candidate === null || candidate === undefined) return "No response";
    if (typeof candidate === "string") {
        const trimmed = candidate.trim();
        if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
            try {
                const parsed = JSON.parse(trimmed);
                const nested = parsed?.response ?? parsed?.summary ?? parsed?.message ?? parsed?.answer;
                if (typeof nested === "string" && nested.trim()) return nested;
            } catch {
                // fall through
            }
        }
        return candidate;
    }
    if (typeof candidate === "object") {
        const nested =
            candidate?.response ??
            candidate?.summary ??
            candidate?.message ??
            candidate?.answer ??
            candidate?.result?.response ??
            candidate?.result?.summary;
        if (typeof nested === "string" && nested.trim()) {
            const trimmed = nested.trim();
            if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
                try {
                    const parsed = JSON.parse(trimmed);
                    const parsedNested = parsed?.response ?? parsed?.summary ?? parsed?.message ?? parsed?.answer;
                    if (typeof parsedNested === "string" && parsedNested.trim()) return parsedNested;
                } catch {
                    // fall through
                }
            }
            return nested;
        }
    }
    try {
        return JSON.stringify(candidate, null, 2);
    } catch {
        return "Unable to parse response";
    }
};

const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString();
};

const createRunSteps = () => ([
    { id: "prompt", label: "Receiving your request", status: "running" },
    { id: "routing", label: "Sending prompt to AI workspace", status: "pending" },
    { id: "analysis", label: "Generating analysis and reasoning", status: "pending" },
    { id: "summary", label: "Preparing final response", status: "pending" },
]);

const serializeMessages = (messages) =>
    messages.map(({ id, role, text, createdAt }) => ({
        id,
        role,
        text,
        createdAt,
    }));

const AiAssistant = () => {
    const adminUser = useSelector((state) => state?.auth?.user);
    const resolvedUserGroup =
        adminUser?.userGroup ??
        adminUser?.group ??
        adminUser?.user_group ??
        adminUser?.usergroup ??
        "trove";

    const persisted = useMemo(() => {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? safeJsonParse(raw) : null;
        return parsed && typeof parsed === "object" ? parsed : {};
    }, []);

    const [conversationId, setConversationId] = useState(persisted.conversationId || uuidv4());
    const [messages, setMessages] = useState(() => {
        const initial = Array.isArray(persisted.messages) ? persisted.messages : [];
        return initial.map((message) => ({ ...message, isTyping: false, fullText: undefined }));
    });
    const [draft, setDraft] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [activeSteps, setActiveSteps] = useState([]);
    const [isDarkMode, setIsDarkMode] = useState(false);

    const stepTimerRef = useRef(null);
    const typingTimerRef = useRef(null);
    const typingMessageIdRef = useRef(null);
    const scrollRef = useRef(null);

    const stopStepTimer = () => {
        if (stepTimerRef.current) {
            clearInterval(stepTimerRef.current);
            stepTimerRef.current = null;
        }
    };

    const stopTyping = () => {
        if (typingTimerRef.current) {
            clearInterval(typingTimerRef.current);
            typingTimerRef.current = null;
        }
        typingMessageIdRef.current = null;
    };

    useEffect(() => {
        return () => {
            stopStepTimer();
            stopTyping();
        };
    }, []);

    useEffect(() => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages, activeSteps]);

    useEffect(() => {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                conversationId,
                messages: serializeMessages(messages).slice(-100),
            })
        );
    }, [conversationId, messages]);

    useEffect(() => {
        if (typingTimerRef.current) return;
        const typingMessage = messages.find((message) => message?.isTyping);
        if (!typingMessage) return;
        if (typingMessageIdRef.current === typingMessage.id) return;

        typingMessageIdRef.current = typingMessage.id;
        const fullText = `${typingMessage.fullText ?? ""}`;
        let index = (typingMessage.text || "").length;

        typingTimerRef.current = setInterval(() => {
            index += 1;
            setMessages((prev) =>
                prev.map((message) => {
                    if (message.id !== typingMessage.id) return message;
                    const nextText = fullText.slice(0, Math.min(index, fullText.length));
                    if (nextText.length >= fullText.length) {
                        return { ...message, text: fullText, isTyping: false, fullText: undefined };
                    }
                    return { ...message, text: nextText };
                })
            );

            if (index >= fullText.length) {
                stopTyping();
            }
        }, 14);

        return () => {
            stopTyping();
        };
    }, [messages]);

    const startStepAnimation = () => {
        const steps = createRunSteps();
        setActiveSteps(steps);
        let index = 0;
        stopStepTimer();
        stepTimerRef.current = setInterval(() => {
            index += 1;
            setActiveSteps((prev) => prev.map((step, stepIndex) => {
                if (stepIndex < index) return { ...step, status: "done" };
                if (stepIndex === index) return { ...step, status: "running" };
                return { ...step, status: "pending" };
            }));
            if (index >= steps.length - 1) {
                stopStepTimer();
            }
        }, 1100);
    };

    const completeSteps = () => {
        stopStepTimer();
        setActiveSteps((prev) => prev.map((step) => ({ ...step, status: "done" })));
    };

    const pushAssistantTypingMessage = (fullText) => {
        setMessages((prev) => [
            ...prev,
            {
                id: uuidv4(),
                role: "assistant",
                text: "",
                fullText,
                isTyping: true,
                createdAt: nowIso(),
            },
        ]);
    };

    const sendPrompt = async (instruction) => {
        const trimmedInstruction = `${instruction || ""}`.trim();
        if (!trimmedInstruction || isSending) return;

        const userMessage = {
            id: uuidv4(),
            role: "user",
            text: trimmedInstruction,
            createdAt: nowIso(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setDraft("");
        setIsSending(true);
        startStepAnimation();

        try {
            const result = await axios.post("/ai/staff-support-message", {
                ref: conversationId,
                message: trimmedInstruction,
                userGroup: resolvedUserGroup,
            });

            completeSteps();
            pushAssistantTypingMessage(normalizeAnswer(result?.data));
        } catch {
            stopStepTimer();
            setActiveSteps((prev) => prev.map((step) => ({ ...step, status: "error" })));
            pushAssistantTypingMessage("No answer at the moment.");
        } finally {
            setIsSending(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        sendPrompt(draft);
    };

    const handleDraftKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            sendPrompt(draft);
        }
    };

    const startNewReview = () => {
        stopStepTimer();
        stopTyping();
        setConversationId(uuidv4());
        setMessages([]);
        setActiveSteps([]);
        setDraft("");
        localStorage.removeItem(STORAGE_KEY);
    };

    const clearConversation = () => {
        stopStepTimer();
        stopTyping();
        setIsSending(false);
        setMessages([]);
        setDraft("");
        setActiveSteps([]);
        localStorage.removeItem(STORAGE_KEY);
    };

    const workspaceTheme = isDarkMode
        ? {
            pageBackground: "#111827",
            cardBackground: "#1F2937",
            panelBackground: "#111827",
            mutedPanelBackground: "#0F172A",
            bubbleBackground: "#243447",
            assistantBubbleBackground: "#1F2937",
            borderColor: "rgba(255,255,255,0.08)",
            primaryText: "#F9FAFB",
            secondaryText: "#D1D5DB",
            mutedText: "#9CA3AF",
        }
        : {
            pageBackground: "transparent",
            cardBackground: "#FFFFFF",
            panelBackground: "#FFFFFF",
            mutedPanelBackground: "#F3F6F9",
            bubbleBackground: "#EEF6FF",
            assistantBubbleBackground: "#F3F6F9",
            borderColor: "rgba(0,0,0,0.08)",
            primaryText: "#181C32",
            secondaryText: "#3F4254",
            mutedText: "#7E8299",
        };

    return (
        <div className="row" style={{ background: workspaceTheme.pageBackground }}>
            <div className="col-lg-12">
                <div
                    className="card card-custom gutter-b"
                    style={{
                        background: workspaceTheme.cardBackground,
                        color: workspaceTheme.primaryText,
                    }}
                >
                    <div className="card-header border-0 py-5">
                        <div className="card-title">
                            <h3 className="card-label">
                                <span className="d-block font-weight-bolder" style={{ color: workspaceTheme.primaryText }}>AI Workspace</span>
                            </h3>
                        </div>
                        <div className="card-toolbar d-flex align-items-center">
                            <div className="d-flex align-items-center mr-6">
                                <span className="font-size-sm mr-3" style={{ color: workspaceTheme.secondaryText }}>
                                    Dark mode
                                </span>
                                <label className="switch switch-sm switch-icon">
                                    <input
                                        type="checkbox"
                                        checked={isDarkMode}
                                        onChange={() => setIsDarkMode((prev) => !prev)}
                                    />
                                    <span />
                                </label>
                            </div>
                            <Button variant="light" className="font-weight-bolder mr-3" onClick={clearConversation} disabled={isSending}>
                                Clear
                            </Button>
                            <Button variant="light" className="font-weight-bolder" onClick={startNewReview} disabled={isSending}>
                                New Conversation
                            </Button>
                        </div>
                    </div>
                    <div className="card-body pt-0">
                        <div className="row">
                            <div className="col-lg-3 mb-6">
                                <div
                                    className="card card-custom card-stretch"
                                    style={{
                                        background: workspaceTheme.mutedPanelBackground,
                                        color: workspaceTheme.primaryText,
                                    }}
                                >
                                    <div className="card-body">
                                        <div className="font-weight-bolder mb-4" style={{ color: workspaceTheme.primaryText }}>Analysis Steps</div>
                                        {!activeSteps.length && (
                                            <div className="font-size-sm" style={{ color: workspaceTheme.mutedText }}>
                                                Ask a question in the workspace. The request stages appear here before the AI finishes the response.
                                            </div>
                                        )}
                                        {!!activeSteps.length && activeSteps.map((step) => (
                                            <div key={step.id} className="d-flex align-items-start mb-4">
                                                <div
                                                    className={`mr-3 rounded-circle ${
                                                        step.status === "done"
                                                            ? "bg-success"
                                                            : step.status === "running"
                                                                ? "bg-primary"
                                                                : step.status === "error"
                                                                    ? "bg-danger"
                                                                    : "bg-secondary"
                                                    }`}
                                                    style={{ width: 10, height: 10, marginTop: 7 }}
                                                />
                                                <div>
                                                    <div className="font-weight-bolder" style={{ color: workspaceTheme.primaryText }}>{step.label}</div>
                                                    <div className="font-size-sm text-uppercase" style={{ color: workspaceTheme.mutedText }}>{step.status}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-9 mb-6">
                                <div
                                    className="card card-custom card-stretch"
                                    style={{
                                        minHeight: "70vh",
                                        background: workspaceTheme.panelBackground,
                                        color: workspaceTheme.primaryText,
                                    }}
                                >
                                    <div className="card-body d-flex flex-column">
                                        <div className="flex-grow-1 pr-2" style={{ overflowY: "auto", maxHeight: "62vh" }}>
                                            {messages.length === 0 && (
                                                <div className="h-100 d-flex flex-column align-items-center justify-content-center text-center px-10">
                                                    <div
                                                        className="d-flex align-items-center justify-content-center mb-5"
                                                        style={{
                                                            width: 72,
                                                            height: 72,
                                                            borderRadius: 999,
                                                            background: "#fff",
                                                            border: `2px solid ${BRAND_GREEN}`,
                                                        }}
                                                    >
                                                        <img
                                                            src={toAbsoluteUrl(CHAT_ICON_URL)}
                                                            alt="Trove"
                                                            style={{ width: 40, height: 40 }}
                                                        />
                                                    </div>
                                                    <div className="font-size-h3 font-weight-bolder mb-3" style={{ color: workspaceTheme.primaryText }}>{ASSISTANT_NAME}</div>
                                                    <div className="font-size-lg" style={{ color: workspaceTheme.mutedText, maxWidth: 520 }}>
                                                        Ask anything. This is your full-screen AI workspace for broader questions, deeper responses, and a more focused chat experience.
                                                    </div>
                                                </div>
                                            )}

                                            {messages.map((message) => (
                                                <div
                                                    key={message.id}
                                                    className={`d-flex mb-6 ${message.role === "user" ? "justify-content-end" : "justify-content-start"}`}
                                                >
                                                    {message.role !== "user" && (
                                                        <div
                                                            className="d-flex align-items-center justify-content-center mr-3"
                                                            style={{
                                                                width: 34,
                                                                height: 34,
                                                                borderRadius: 999,
                                                                background: "#fff",
                                                                flexShrink: 0,
                                                                marginTop: 2,
                                                                border: `2px solid ${BRAND_GREEN}`,
                                                            }}
                                                        >
                                                            <img
                                                                src={toAbsoluteUrl(CHAT_ICON_URL)}
                                                                alt="Trove"
                                                                style={{ width: 20, height: 20 }}
                                                            />
                                                        </div>
                                                    )}
                                                    <div
                                                        className="p-5 rounded"
                                                        style={{
                                                            maxWidth: "84%",
                                                            whiteSpace: "pre-wrap",
                                                            lineHeight: 1.7,
                                                            color: message.role === "user" ? workspaceTheme.primaryText : workspaceTheme.secondaryText,
                                                            background: message.role === "user"
                                                                ? workspaceTheme.bubbleBackground
                                                                : workspaceTheme.assistantBubbleBackground,
                                                            border: `1px solid ${workspaceTheme.borderColor}`,
                                                        }}
                                                    >
                                                        <div className="font-weight-bolder mb-2">
                                                            {message.role === "user" ? "You" : ASSISTANT_NAME}
                                                        </div>
                                                        <div>{message.text}</div>
                                                        <div className="font-size-xs mt-3" style={{ color: workspaceTheme.mutedText }}>
                                                            {formatDateTime(message.createdAt)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {isSending && (
                                                <div className="d-flex mb-6 justify-content-start">
                                                    <div
                                                        className="d-flex align-items-center justify-content-center mr-3"
                                                        style={{
                                                            width: 34,
                                                            height: 34,
                                                            borderRadius: 999,
                                                            background: "#fff",
                                                            flexShrink: 0,
                                                            marginTop: 2,
                                                            border: `2px solid ${BRAND_GREEN}`,
                                                        }}
                                                    >
                                                        <img
                                                            src={toAbsoluteUrl(CHAT_ICON_URL)}
                                                            alt="Trove"
                                                            style={{ width: 20, height: 20 }}
                                                        />
                                                    </div>
                                                    <div
                                                        className="p-5 rounded"
                                                        style={{
                                                            maxWidth: "84%",
                                                            background: workspaceTheme.assistantBubbleBackground,
                                                            border: `1px solid ${workspaceTheme.borderColor}`,
                                                        }}
                                                    >
                                                        <span className="font-size-sm" style={{ color: workspaceTheme.primaryText }}>
                                                            <span style={{ opacity: 0.5 }}>Typing</span>
                                                            <span style={{ letterSpacing: 2 }}>...</span>
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                            <div ref={scrollRef} />
                                        </div>

                                        <form onSubmit={handleSubmit} className="border-top pt-6 mt-6">
                                            <div className="d-flex align-items-center">
                                                <textarea
                                                    className="form-control border-0"
                                                    rows={2}
                                                    value={draft}
                                                    onChange={(event) => setDraft(event.target.value)}
                                                    onKeyDown={handleDraftKeyDown}
                                                    placeholder="Type your message..."
                                                    disabled={isSending}
                                                    style={{
                                                        resize: "none",
                                                        boxShadow: "none",
                                                        background: "transparent",
                                                        color: workspaceTheme.primaryText,
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-icon btn-sm"
                                                    title="New conversation"
                                                    onClick={startNewReview}
                                                    disabled={isSending}
                                                >
                                                    <span className="svg-icon svg-icon-md">
                                                        <SVG src={toAbsoluteUrl("/media/svg/icons/Layout/Layout-4-blocks.svg")} />
                                                    </span>
                                                </button>
                                                <Button type="submit" className="ml-3 px-8" disabled={isSending || !draft.trim()}>
                                                    {isSending ? "Thinking..." : "Send"}
                                                </Button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AiAssistant;
